/**
 * Daily reminder notifications for Beloved.
 *
 * This module is SAFE TO IMPORT ON THE WEB. `@capacitor/core` provides a web
 * shim, and the native LocalNotifications plugin is only ever dynamically
 * imported after we confirm we are running inside the native app. On the web,
 * native scheduling functions are no-ops that resolve to `false` — nothing
 * throws, and the OS permission prompt is never triggered. Time preferences
 * remain harmless device-local settings on every platform.
 *
 * UX rule (binding): the permission prompt must NEVER fire on first launch.
 * The Today tab shows a gentle opt-in card once; only when the person taps
 * "Turn on" do we call {@link enableDailyReminders}, which is the single place
 * that asks the operating system for permission.
 */

import { Capacitor } from '@capacitor/core';

/** localStorage key that remembers the opt-in card has been shown/dismissed. */
export const REMINDER_PROMPT_KEY = 'beloved_reminder_prompt';

/** localStorage key for the person's preferred morning and evening times. */
export const REMINDER_TIMES_KEY = 'beloved_reminder_times';

/** Default local times used until the person chooses their own. */
export const DEFAULT_REMINDER_TIMES = Object.freeze({
  morning: '08:00',
  evening: '20:30',
});

/**
 * The two daily reminders, with stable notification ids, default times, and
 * copy. The person may replace the defaults in Settings.
 */
export const DAILY_REMINDERS = [
  {
    id: 1001,
    hour: 8,
    minute: 0,
    title: 'Beloved',
    body: "Today's Scripture is ready",
  },
  {
    id: 1002,
    hour: 20,
    minute: 30,
    title: 'Beloved',
    body: 'Take a quiet moment before the day ends',
  },
];

/**
 * Keep a time in safe 24-hour HH:MM form, or use the supplied fallback.
 * @param {unknown} value
 * @param {string} fallback
 * @returns {string}
 */
function normaliseTime(value, fallback) {
  if (typeof value !== 'string') return fallback;
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return fallback;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return fallback;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Normalise a possibly-partial reminder-time object.
 * @param {unknown} times
 * @returns {{morning: string, evening: string}}
 */
export function normaliseReminderTimes(times) {
  const value = times && typeof times === 'object' ? times : {};
  return {
    morning: normaliseTime(value.morning, DEFAULT_REMINDER_TIMES.morning),
    evening: normaliseTime(value.evening, DEFAULT_REMINDER_TIMES.evening),
  };
}

/** Read the person's preferred local reminder times. */
export function getReminderTimes() {
  try {
    const raw = window.localStorage.getItem(REMINDER_TIMES_KEY);
    return normaliseReminderTimes(raw ? JSON.parse(raw) : null);
  } catch (e) {
    return { ...DEFAULT_REMINDER_TIMES };
  }
}

/** Persist preferred local reminder times and return the normalised result. */
export function saveReminderTimes(times) {
  const next = normaliseReminderTimes(times);
  try {
    window.localStorage.setItem(REMINDER_TIMES_KEY, JSON.stringify(next));
  } catch (e) {
    // A blocked store simply means the defaults return next time.
  }
  return next;
}

/**
 * Apply preferred times to the two stable reminder definitions.
 * @param {{morning?: string, evening?: string}} [times]
 */
export function buildDailyReminders(times = getReminderTimes()) {
  const selected = normaliseReminderTimes(times);
  return DAILY_REMINDERS.map((reminder, index) => {
    const value = index === 0 ? selected.morning : selected.evening;
    const [hour, minute] = value.split(':').map(Number);
    return { ...reminder, hour, minute };
  });
}

/**
 * True only when running inside the native Capacitor app (iOS/Android).
 * On the web this is always false, which keeps every reminder feature dormant.
 * @returns {boolean}
 */
export function isNotificationsSupported() {
  return Capacitor.isNativePlatform();
}

/**
 * Dynamically load the native LocalNotifications plugin.
 * Kept out of the module's static imports so the web bundle and jest never
 * need the native plugin present.
 * @returns {Promise<import('@capacitor/local-notifications').LocalNotificationsPlugin>}
 */
async function loadPlugin() {
  const mod = await import('@capacitor/local-notifications');
  return mod.LocalNotifications;
}

/**
 * Whether the one-time opt-in card should be shown on the Today tab.
 * Shown only on native, and only until the person has answered once
 * (either "Turn on" or "Later" records a dismissal).
 * @returns {boolean}
 */
export function shouldShowReminderPrompt() {
  if (!isNotificationsSupported()) return false;
  try {
    return window.localStorage.getItem(REMINDER_PROMPT_KEY) === null;
  } catch (e) {
    // If storage is unavailable, err on the side of not nagging.
    return false;
  }
}

/**
 * Record that the person has answered the opt-in card, so it is never shown
 * again. Called for both "Turn on" and "Later".
 * @param {'on'|'later'} [choice] - what the person chose (for our own record).
 * @returns {void}
 */
export function dismissReminderPrompt(choice = 'later') {
  try {
    window.localStorage.setItem(REMINDER_PROMPT_KEY, choice);
  } catch (e) {
    // A blocked storage just means the card may reappear next launch; harmless.
  }
}

/**
 * Ask the operating system for permission to show notifications.
 * No-op on the web. This is only ever reached after an explicit tap.
 * @returns {Promise<boolean>} true if permission was granted.
 */
export async function requestNotificationPermission() {
  if (!isNotificationsSupported()) return false;
  try {
    const LocalNotifications = await loadPlugin();
    const result = await LocalNotifications.requestPermissions();
    return result.display === 'granted';
  } catch (e) {
    return false;
  }
}

/**
 * Schedule the two repeating daily reminders at the person's chosen times.
 * Assumes permission has already been granted. No-op on the web.
 * @returns {Promise<boolean>} true if the reminders were scheduled.
 */
export async function scheduleDailyReminders(times = getReminderTimes()) {
  if (!isNotificationsSupported()) return false;
  try {
    const LocalNotifications = await loadPlugin();
    await LocalNotifications.schedule({
      notifications: buildDailyReminders(times).map((reminder) => ({
        id: reminder.id,
        title: reminder.title,
        body: reminder.body,
        schedule: {
          on: { hour: reminder.hour, minute: reminder.minute },
          allowWhileIdle: true,
        },
      })),
    });
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Save new times and, when reminders are already on, replace the scheduled
 * notifications immediately without asking for permission again.
 */
export async function rescheduleDailyReminders(times) {
  const selected = saveReminderTimes(times);
  if (!isNotificationsSupported() || !remindersEnabled()) return false;
  const cancelled = await cancelDailyReminders();
  if (!cancelled) return false;
  return scheduleDailyReminders(selected);
}

/**
 * Cancel any scheduled daily reminders. No-op on the web.
 * @returns {Promise<boolean>} true if the cancel request was sent.
 */
export async function cancelDailyReminders() {
  if (!isNotificationsSupported()) return false;
  try {
    const LocalNotifications = await loadPlugin();
    await LocalNotifications.cancel({
      notifications: DAILY_REMINDERS.map((reminder) => ({ id: reminder.id })),
    });
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Turn on daily reminders: request permission, then (if granted) schedule them.
 * This is the ONLY function that triggers the OS permission prompt, and it is
 * called only from the opt-in card's "Turn on" button (or the Settings toggle).
 * @returns {Promise<boolean>} true if reminders are now on.
 */
export async function enableDailyReminders() {
  const granted = await requestNotificationPermission();
  if (!granted) return false;
  return scheduleDailyReminders();
}

/**
 * Turn off daily reminders: cancel both scheduled notifications and remember
 * the "off" state so Settings shows the toggle as off.
 * No-op on the web (nothing was ever scheduled there), but the "off" state is
 * still recorded harmlessly. Never throws.
 * @returns {Promise<boolean>} true if the cancel request was sent to the OS.
 */
export async function disableDailyReminders() {
  // Record the off state first so the toggle reflects it immediately, even if
  // the native cancel below is unavailable for any reason.
  try {
    window.localStorage.setItem(REMINDER_PROMPT_KEY, 'off');
  } catch (e) {
    // A blocked storage just means the choice won't persist; harmless.
  }
  if (!isNotificationsSupported()) return false;
  try {
    const LocalNotifications = await loadPlugin();
    await LocalNotifications.cancel({
      notifications: DAILY_REMINDERS.map((reminder) => ({ id: reminder.id })),
    });
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Whether daily reminders are currently on. Derived from the single stored
 * value ({@link REMINDER_PROMPT_KEY} === 'on'), so the UI never has to duplicate
 * the key. Always false on the web, where reminders are dormant.
 * @returns {boolean}
 */
export function remindersEnabled() {
  if (!isNotificationsSupported()) return false;
  try {
    return window.localStorage.getItem(REMINDER_PROMPT_KEY) === 'on';
  } catch (e) {
    return false;
  }
}
